import { useState } from "react";
import { Link } from "react-router";
import { ConfirmDialog } from "../components/Dialog";
import { useCampaign, useDraft } from "./context";
import { CampaignError, SecretBadge, useEntries } from "./parts";
import { AttendeesField, BeginSessionDialog, CloseSessionDialog, dayOf, fieldOf, NoteDialog, realDate, today, useChoiceLabel } from "./play";
import type { CampaignEntryListItem } from "./types";

/** The sessions with their status: begin one, note and close the running one, add one played without the app afterwards. */
export function SessionsPage() {
  const campaign = useCampaign();
  const sessions = useEntries({ kind: "session" });
  const [dialog, setDialog] = useState<"begin" | "later" | null>(null);
  if (!campaign.canWrite) return <p className="muted">Diese Seite gibt es in dieser Ansicht nicht.</p>;
  const term = campaign.term("session");
  const sorted = [...(sessions.data ?? [])].sort((a, b) => {
    const running = Number(fieldOf(b, "status") === "running") - Number(fieldOf(a, "status") === "running");
    return running || String(fieldOf(b, "date") ?? "").localeCompare(String(fieldOf(a, "date") ?? ""));
  });
  return (
    <section className="stack">
      <h2>{term}</h2>
      <div className="actions">
        <button type="button" className="button primary" onClick={() => setDialog("begin")}>
          {term} beginnen
        </button>
        <button type="button" className="button secondary" onClick={() => setDialog("later")}>
          Nachtragen
        </button>
      </div>
      {sessions.error && <CampaignError error={sessions.error} reload />}
      {sessions.isLoading && <p className="muted">Lade …</p>}
      {sessions.data && sorted.length === 0 && <p className="muted">Noch keine.</p>}
      <ul className="plain-list stack" aria-label={term}>
        {sorted.map((s) => (
          <SessionRow key={s.id} session={s} />
        ))}
      </ul>
      {dialog === "begin" && <BeginSessionDialog onClose={() => setDialog(null)} />}
      {dialog === "later" && <LaterSessionDialog onClose={() => setDialog(null)} />}
    </section>
  );
}

function SessionRow({ session }: { session: CampaignEntryListItem }) {
  const campaign = useCampaign();
  const label = useChoiceLabel();
  const [dialog, setDialog] = useState<"note" | "close" | null>(null);
  const status = fieldOf(session, "status");
  const from = dayOf(session, "dayFrom");
  const to = dayOf(session, "dayTo");
  const days = from ? (to && to.number !== from.number ? `${from.date.de} – ${to.date.de}` : from.date.de) : null;
  return (
    <li className="card stack" aria-label={session.title}>
      <div className="row">
        <h3 className="grow">
          <Link to={campaign.entryPath(session.id)}>{session.title}</Link>
        </h3>
        <SecretBadge visibility={session.visibility} />
        <span className="badge">{status ? label("session", "status", status) : "vorbereitet"}</span>
      </div>
      <p className="muted small">{[realDate(fieldOf(session, "date")), days].filter(Boolean).join(" · ")}</p>
      {status === "running" && (
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setDialog("note")}>
            Notiz
          </button>
          <button type="button" className="button primary" onClick={() => setDialog("close")}>
            Abschließen
          </button>
        </div>
      )}
      {dialog === "note" && <NoteDialog onClose={() => setDialog(null)} />}
      {dialog === "close" && <CloseSessionDialog sessionId={session.id} title={session.title} onClose={() => setDialog(null)} />}
    </li>
  );
}

/** A session played without the app, added afterwards as closed, with its summary in the same operation. */
function LaterSessionDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const adventures = (useEntries({ kind: "adventure" }).data ?? []).filter((a) => fieldOf(a, "status") !== "planned");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today);
  const [attendees, setAttendees] = useState(() => campaign.head.party.map((p) => p.id));
  const [adventure, setAdventure] = useState("");
  const [summary, setSummary] = useState("");
  const term = campaign.term("session");
  useDraft(title.trim() !== "" || summary.trim() !== "" || adventure !== "");
  return (
    <ConfirmDialog
      title={`${term} nachtragen`}
      action="Nachtragen"
      onClose={onClose}
      onConfirm={() => {
        const fields: Record<string, unknown> = { status: "closed", date, attendees };
        if (adventure) fields.adventure = adventure;
        const payload: Record<string, unknown> = { kind: "session", title: title.trim() || `${term} am ${realDate(date)}`, fields };
        if (summary.trim()) payload.sections = { summary };
        return campaign.apply({ type: "campaign.entry.create", payload });
      }}
    >
      <label className="field">
        <span>Titel</span>
        <input value={title} placeholder={`${term} am ${realDate(date)}`} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </label>
      <label className="field">
        <span>Datum</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <AttendeesField value={attendees} onChange={setAttendees} />
      {adventures.length > 0 && (
        <label className="field">
          <span>{campaign.term("adventure")}</span>
          <select value={adventure} onChange={(e) => setAdventure(e.target.value)}>
            <option value="">–</option>
            {adventures.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span>Zusammenfassung (für alle sichtbar)</span>
        <textarea rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </label>
    </ConfirmDialog>
  );
}
